import { css, cx } from "@emotion/css";
import { ActionButton, ActionButtonType, Drawer } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { KubernetesLiveApplicationStatusCode, KubernetesLiveStatusResource, TaskState, TimelineItem } from "@octopusdeploy/octopus-server-client";
import { isPodResource } from "@octopusdeploy/octopus-server-client";
import React, { useEffect, useMemo, useState } from "react";
import type { ViewKubernetesObjectDrawerEvent } from "~/analytics/Analytics";
import { useAnalyticViewKubernetesObjectDrawerDispatch } from "~/analytics/Analytics";
import { EventsTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/EventsTab";
import { LogsTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/LogsTab";
import { ManifestTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/ManifestTab";
import { SummaryTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/SummaryTab";
import { flattenResourceHierarchy } from "~/areas/projects/components/Observability/ObjectDrawer/flattenResourceHierarchy";
import { ControlledTabsContainer, TabItem } from "~/primitiveComponents/navigation/Tabs/index";
type PartialViewKubernetesObjectDrawerEvent = {
    releaseId: string;
    deploymentStatus: TaskState;
    liveApplicationHealth: KubernetesLiveApplicationStatusCode;
    executionType: "deployment";
};
type LiveStatusObjectDrawerProps = {
    environmentId: string;
    projectId: string;
    tenantId?: string;
    resource: KubernetesLiveStatusResource | undefined;
    latestDeployment: TimelineItem;
    analyticEventData: PartialViewKubernetesObjectDrawerEvent;
    onClose: () => void;
};
const AllTabs = ["Summary", "Logs", "Events", "Manifest"];
type TabsTuple = typeof AllTabs;
type Tabs = TabsTuple[number];
const styles = {
    mainContainer: css({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        position: "relative",
    }),
    tabItemContainer: css({
        padding: space[16],
        paddingBottom: space[32],
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        overflowY: "auto",
    }),
    logsEventsTabItemContainer: css({
        //the logs & events containers needs to handle their own left and right padding for the line hover
        paddingLeft: 0,
        paddingRight: 0,
    }),
};
export const LiveStatusObjectDrawer = (props: LiveStatusObjectDrawerProps) => {
    const { onClose, resource, projectId, environmentId, latestDeployment, analyticEventData } = props;
    const [activeTab, setActiveTab] = useState<Tabs>("Summary");
    const [hasViewedManifestTab, setHasViewedManifestTab] = useState(false);
    //always reset to the summary tab when opening
    useEffect(() => {
        if (props.resource) {
            setActiveTab("Summary");
            setHasViewedManifestTab(false);
        }
    }, [props.resource]);
    const { result, isLoading, error: detailedResourceError, } = useInlineStatusQuery((repo) => {
        if (!props.resource) {
            return Promise.resolve(null);
        }
        const id = props.resource.DesiredResourceId || props.resource.ResourceId;
        if (!id) {
            return Promise.reject("The live resource does not have a DesiredResourceId or ResourceId");
        }
        return repo.KubernetesLiveStatus.getResource(props.projectId, props.environmentId, props.tenantId, props.resource.MachineId, id);
    }, [props.environmentId, props.projectId, props.resource, props.tenantId], "Get live status resource details");
    const detailedResource = result?.Resource;
    const { result: manifest, isLoading: isLoadingManifest, error: manifestError, } = useInlineStatusQuery((repo) => {
        //if we haven't viewed the manifest tab yet, don't do anything
        if (!props.resource || !hasViewedManifestTab) {
            return Promise.resolve(null);
        }
        const id = props.resource.DesiredResourceId || props.resource.ResourceId;
        if (!id) {
            return Promise.reject("The live resource does not have a DesiredResourceId or ResourceId");
        }
        return repo.KubernetesLiveStatus.getResourceManifest(props.projectId, props.environmentId, props.tenantId, props.resource.MachineId, id);
    }, [props.environmentId, props.projectId, props.resource, props.tenantId, hasViewedManifestTab], "Get live status resource manifest");
    const showLogsTab = useMemo(() => {
        if (!detailedResource) {
            return false;
        }
        const allResources = flattenResourceHierarchy(detailedResource);
        //we show the Logs tab if any resource in the hierarchy is a Pod resource
        return allResources?.some((r) => r && isPodResource(r)) || false;
    }, [detailedResource]);
    const onManifestTabViewed = () => {
        if (!hasViewedManifestTab) {
            setHasViewedManifestTab(true);
        }
    };
    const manifestAnalyticEventData = detailedResource &&
        resource && {
        ...analyticEventData,
        projectId: projectId,
        environmentId: environmentId,
        deploymentId: latestDeployment.Id,
        objectHealth: detailedResource.HealthStatus,
        objectSyncStatus: detailedResource.SyncStatus,
        tab: activeTab,
        objectId: resource.ResourceId || "unknown",
        objectType: resource.Kind,
    };
    return (<Drawer variant={"customActions"} isOpen={!!resource} onClose={onClose} customActions={[<ActionButton key={`Cancel ${resource?.Name}`} type={ActionButtonType.Secondary} label="Close" accessibleName={`Close ${resource?.Name}}`} onClick={onClose}/>]} title={resource?.Name || ""} subtitle={`${resource?.Kind}${resource?.Namespace ? ` in ${resource.Namespace}` : ""}`} width={"wide"}>
            {resource && (<div className={styles.mainContainer}>
                    {detailedResource && (<LiveStatusObjectDrawerAnalyticView eventData={{
                    ...analyticEventData,
                    projectId: projectId,
                    environmentId: environmentId,
                    deploymentId: latestDeployment.Id,
                    objectHealth: detailedResource.HealthStatus,
                    objectSyncStatus: detailedResource.SyncStatus,
                    tab: activeTab,
                    objectId: resource.ResourceId || "unknown",
                    objectType: resource.Kind,
                }}/>)}
                    <ControlledTabsContainer value={activeTab} onChange={(tab) => setActiveTab(tab)}>
                        <TabItem value={"Summary"} label={"Summary"}>
                            <div className={styles.tabItemContainer}>
                                <SummaryTab detailedResource={detailedResource} isLoading={isLoading} error={detailedResourceError}/>
                            </div>
                        </TabItem>
                        <TabItem label={"Events"} value={"Events"}>
                            <div className={cx(styles.tabItemContainer, styles.logsEventsTabItemContainer)}>
                                <EventsTab detailedResource={detailedResource} projectId={props.projectId} environmentId={props.environmentId} tenantId={props.tenantId}/>
                            </div>
                        </TabItem>
                        {showLogsTab && (<TabItem label={"Logs"} value={"Logs"}>
                                <div className={cx(styles.tabItemContainer, styles.logsEventsTabItemContainer)}>
                                    <LogsTab detailedResource={detailedResource} projectId={props.projectId} environmentId={props.environmentId} tenantId={props.tenantId}/>
                                </div>
                            </TabItem>)}
                        <TabItem label={"Manifest"} value={"Manifest"} onActive={onManifestTabViewed}>
                            <div className={styles.tabItemContainer}>
                                {manifestAnalyticEventData && (<ManifestTab detailedResource={detailedResource} liveManifest={manifest?.LiveManifest ?? null} desiredManifest={manifest?.DesiredManifest ?? null} isLoading={isLoadingManifest} error={manifestError} diff={manifest?.Diff ?? null} latestDeployment={latestDeployment} analyticEventData={manifestAnalyticEventData}/>)}
                            </div>
                        </TabItem>
                    </ControlledTabsContainer>
                </div>)}
        </Drawer>);
};
function LiveStatusObjectDrawerAnalyticView({ eventData }: {
    eventData: ViewKubernetesObjectDrawerEvent;
}) {
    const dispatch = useAnalyticViewKubernetesObjectDrawerDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch("View Kubernetes Object Tab", eventData), [eventData.tab]);
    return <></>;
}
